import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  LogOut, Package, Camera, CheckCircle2, ChevronRight, Radio,
  Navigation, ArrowUp, ArrowDown, RotateCcw, Undo2, Info, FileText, AlertTriangle, X, MapPin, User,
} from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import BrandLogo from "@/components/BrandLogo";

const ACTIVE = ["assigned", "picked_up", "in_transit"];
const NEXT_STATUS = { assigned: "picked_up", picked_up: "in_transit", in_transit: "delivered" };
const CRITICAL = ["Direct", "HOT", "Rush"];
const isReattempt = (job) => (job.history || []).some((h) => h.status === "delivered");

function StatusBadge({ status }) {
  const map = {
    assigned: "text-sky-300 border-sky-500 bg-sky-500/10",
    picked_up: "text-violet-300 border-violet-500 bg-violet-500/10",
    in_transit: "text-emerald-300 border-emerald-500 bg-emerald-500/10",
    delivered: "text-emerald-400 border-emerald-500 bg-emerald-500/10",
  };
  return <span className={`font-mono text-xs uppercase tracking-widest px-2.5 py-1 border rounded ${map[status] || "text-slate-300 border-slate-600"}`}>{status.replace("_", " ")}</span>;
}

function PriorityTag({ priority }) {
  const critical = CRITICAL.includes(priority);
  return (
    <span className={`font-mono text-xs font-black uppercase tracking-[0.2em] px-2.5 py-1 border rounded ${critical ? "border-red-500 text-white bg-red-600" : "border-slate-600 text-slate-300 bg-slate-800"}`}>
      {priority}
    </span>
  );
}

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [signature, setSignature] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState("");
  const [podStep, setPodStep] = useState(0); // 0 closed, 1 signature+name, 2 photo
  const [busy, setBusy] = useState(false);
  const [detailsJob, setDetailsJob] = useState(null);
  const [pickupNavigated, setPickupNavigated] = useState({}); // job.id -> true once "Nav to Pickup" tapped

  const handleNavToPickup = (job) => {
    openMaps(job.pickup);
    setPickupNavigated((p) => ({ ...p, [job.id]: true }));
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/deliveries");
      setJobs(data.deliveries);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  // Stream the driver's REAL device location to dispatch while the dashboard is open.
  useEffect(() => {
    if (!navigator.geolocation) {
      toast.error("Location not supported on this device/browser");
      return;
    }
    const post = (pos) => {
      const { latitude, longitude } = pos.coords;
      api.post("/drivers/location", { lat: latitude, lng: longitude }).catch(() => {});
    };
    const onErr = (err) => {
      if (err.code === err.PERMISSION_DENIED) {
        toast.error("Enable location so dispatch can see you on the map");
      }
    };
    navigator.geolocation.getCurrentPosition(post, onErr, { enableHighAccuracy: true, timeout: 10000 });
    const watchId = navigator.geolocation.watchPosition(post, onErr, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const active = jobs.filter((j) => ACTIVE.includes(j.status));
  const delivered = jobs.filter((j) => j.status === "delivered");

  // Lock ALL page scrolling while a stop is in the "Arrived — Deliver" state (in_transit),
  // until that delivery is completed. Only what fits on screen is shown.
  const deliverLock = active.some((j) => j.status === "in_transit" && !isReattempt(j));
  useEffect(() => {
    if (deliverLock) {
      const html = document.documentElement;
      const prevHtml = html.style.overflow;
      const prevBody = document.body.style.overflow;
      html.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      return () => { html.style.overflow = prevHtml; document.body.style.overflow = prevBody; };
    }
  }, [deliverLock]);

  const advance = async (job) => {
    const next = NEXT_STATUS[job.status];
    if (!next) return;
    if (next === "delivered") {
      setActiveJob(job);
      setSignature(null); setRecipientName(""); setPhoto(null); setNotes("");
      setPodStep(1);
      return;
    }
    try {
      await api.post(`/deliveries/${job.id}/status`, { status: next });
      toast.success(`Updated to ${next.replace("_", " ")}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reorder = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= active.length) return;
    const ids = active.map((j) => j.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      const { data } = await api.post("/driver/reorder", { deliveries: ids });
      if (data.status === "pending_approval") {
        toast.info("Critical priority — sent to dispatcher for approval", { description: "Order stays as-is until approved." });
      } else if (data.status === "applied") {
        toast.success("Route reordered", { description: "Dispatcher notified." });
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const revisit = async (job) => {
    const activeReattempt = jobs.find((j) => j.status === "in_transit" && isReattempt(j));
    if (activeReattempt && activeReattempt.id !== job.id) {
      toast.error("Only one revisit at a time", { description: `Cancel the active second attempt (${activeReattempt.delivery_number}) first.` });
      document.getElementById(`job-${activeReattempt.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      const { data } = await api.post(`/deliveries/${job.id}/revisit`);
      toast.success(data.delivery.status === "picked_up"
        ? "Deferred — moved to end of route"
        : "Reopened for re-attempt — moved to top");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancelReattempt = async (job) => {
    try {
      await api.post(`/deliveries/${job.id}/cancel-revisit`);
      toast.success("Second attempt cancelled");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openMaps = (pt) => {
    const dest = (pt.lat && pt.lng) ? `${pt.lat},${pt.lng}` : encodeURIComponent(pt.address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, "_blank");
  };

  const revertPickup = async (job) => {
    try {
      await api.post(`/deliveries/${job.id}/status`, { status: "assigned" });
      toast.success("Reverted — marked as not picked up");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startTransit = async (job) => {
    openMaps(job.dropoff); // fire nav on the click gesture to avoid popup blocking
    try {
      await api.post(`/deliveries/${job.id}/status`, { status: "in_transit" });
      toast.success("In transit — navigating to dropoff");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const continueToPhoto = () => {
    if (!signature) { toast.error("Capture a signature first"); return; }
    if (!recipientName.trim()) { toast.error("Enter recipient name"); return; }
    setPodStep(2);
  };

  const submitProof = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/${activeJob.id}/proof`, {
        recipient_name: recipientName.trim(),
        signature_data_url: signature,
        photo_data_url: photo,
        notes: notes || null,
      });
      toast.success("Delivery confirmed");
      setPodStep(0); setActiveJob(null);
      setSignature(null); setRecipientName(""); setPhoto(null); setNotes("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  // Primary action per status (large, high-contrast)
  const primaryBtn = "w-full min-h-[64px] rounded-xl bg-[#E56A00] hover:bg-[#CC5A00] text-white text-xl font-bold uppercase tracking-wide shadow-[0_4px_14px_0_rgba(229,106,0,0.4)] transition-transform active:scale-[0.97] flex items-center justify-center gap-2 touch-manipulation";
  const secondaryBtn = "min-h-[64px] rounded-xl border-2 border-slate-600 bg-slate-800/60 text-slate-200 hover:bg-slate-700 text-base font-bold uppercase tracking-wide transition-transform active:scale-[0.97] flex items-center justify-center gap-2 touch-manipulation";
  const lockedBtn = "w-full min-h-[64px] rounded-xl border-2 border-slate-700 bg-slate-800/40 text-slate-500 text-xl font-bold uppercase tracking-wide flex items-center justify-center gap-2 cursor-not-allowed";

  const AddressBlock = ({ job, big }) => (
    <div className="space-y-4">
      <div className="flex gap-3 items-start">
        <MapPin className="w-6 h-6 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.4} />
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Pickup</div>
          <div className={`${big ? "text-2xl sm:text-3xl" : "text-xl"} font-bold leading-tight text-white break-words`}>{job.pickup.address}</div>
          {job.pickup.contact_name && (
            <div className={`flex items-center gap-1.5 mt-1 ${big ? "text-lg" : "text-base"} font-medium text-slate-300 flex-wrap`}>
              <User className="w-4 h-4 text-slate-500 shrink-0" /><span>{job.pickup.contact_name}</span>
              {job.pickup.contact_phone && (<>
                <span className="text-slate-500">·</span>
                <a href={`tel:${job.pickup.contact_phone}`} onClick={(e) => e.stopPropagation()} className="text-amber-300 underline underline-offset-4 active:text-amber-200" data-testid={`call-pickup-${job.delivery_number}`}>{job.pickup.contact_phone}</a>
              </>)}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <MapPin className="w-6 h-6 text-[#FF8A3D] mt-0.5 shrink-0" strokeWidth={2.4} />
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Dropoff</div>
          <div className={`${big ? "text-2xl sm:text-3xl" : "text-xl"} font-bold leading-tight text-white break-words`}>{job.dropoff.address}</div>
          {job.dropoff.contact_name && (
            <div className={`flex items-center gap-1.5 mt-1 ${big ? "text-lg" : "text-base"} font-medium text-slate-300 flex-wrap`}>
              <User className="w-4 h-4 text-slate-500 shrink-0" /><span>{job.dropoff.contact_name}</span>
              {job.dropoff.contact_phone && (<>
                <span className="text-slate-500">·</span>
                <a href={`tel:${job.dropoff.contact_phone}`} onClick={(e) => e.stopPropagation()} className="text-amber-300 underline underline-offset-4 active:text-amber-200" data-testid={`call-dropoff-${job.delivery_number}`}>{job.dropoff.contact_phone}</a>
              </>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const Actions = ({ job }) => (
    <>
      {job.status === "assigned" && (() => {
        const navDone = !!pickupNavigated[job.id];
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => handleNavToPickup(job)} className={navDone ? `${secondaryBtn} min-h-[64px] w-full text-xl` : primaryBtn} data-testid={`nav-btn-${job.delivery_number}`}>
              <Navigation className="w-5 h-5" />Nav to Pickup
            </button>
            <button onClick={() => advance(job)} disabled={!navDone} className={navDone ? primaryBtn : lockedBtn} data-testid={`advance-btn-${job.delivery_number}`}>
              Mark Picked Up <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        );
      })()}
      {job.status === "picked_up" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={() => revertPickup(job)} className={`${secondaryBtn} border-amber-500/60 text-amber-300 hover:bg-amber-500/10`} data-testid={`not-picked-up-btn-${job.delivery_number}`}>
            <Undo2 className="w-5 h-5" />Not Picked Up
          </button>
          <button onClick={() => startTransit(job)} className={primaryBtn} data-testid={`start-transit-btn-${job.delivery_number}`}>
            <Navigation className="w-5 h-5" />Nav to Dropoff
          </button>
        </div>
      )}
      {job.status === "in_transit" && (
        isReattempt(job) ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => startTransit(job)} className={`${primaryBtn} bg-amber-500 hover:bg-amber-600 shadow-[0_4px_14px_0_rgba(245,158,11,0.4)]`} data-testid={`start-transit-btn-${job.delivery_number}`}>
                <Navigation className="w-5 h-5" />Nav to Dropoff
              </button>
              <button onClick={() => advance(job)} className={`${primaryBtn} bg-amber-500 hover:bg-amber-600 shadow-[0_4px_14px_0_rgba(245,158,11,0.4)]`} data-testid={`advance-btn-${job.delivery_number}`}>
                Second Attempt <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            <button onClick={() => cancelReattempt(job)} className={`${secondaryBtn} w-full border-red-500/60 text-red-300 hover:bg-red-500/10`} data-testid={`cancel-reattempt-btn-${job.delivery_number}`}>
              <X className="w-5 h-5" />Cancel Trip
            </button>
          </div>
        ) : (
          <button onClick={() => advance(job)} className={`${primaryBtn} bg-emerald-500 hover:bg-emerald-600 shadow-[0_4px_14px_0_rgba(16,185,129,0.4)]`} data-testid={`advance-btn-${job.delivery_number}`}>
            <CheckCircle2 className="w-6 h-6" />Arrived — Deliver
          </button>
        )
      )}
    </>
  );

  return (
    <div className="driver-dashboard bg-[#0B1120] text-slate-50 min-h-screen w-full flex flex-col">
      <header className="border-b border-slate-800 px-4 py-4 flex items-center justify-between bg-[#0F172A]/95 backdrop-blur-xl sticky top-0 z-20 shadow-md">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-9" />
          <div>
            <div className="font-display text-base uppercase tracking-tight text-white">DRIVER // {user.name}</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-1.5"><Radio className="w-3 h-3 animate-pulse" /> ONLINE · {active.length} JOBS</div>
          </div>
        </div>
        <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="min-h-[48px] font-mono uppercase text-xs bg-slate-800/60 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Out</Button>
      </header>

      <div className="flex-1 p-4 md:p-6 space-y-4 max-w-2xl mx-auto w-full pb-28">
        {active.length === 0 && delivered.length === 0 && (
          <div className="border-2 border-dashed border-slate-700 rounded-2xl p-12 text-center bg-slate-900/40" data-testid="no-jobs">
            <Package className="w-14 h-14 mx-auto mb-4 text-slate-600" strokeWidth={1.5} />
            <div className="font-display uppercase text-2xl tracking-tight mb-2 text-white">NO JOBS YET</div>
            <div className="font-mono text-sm uppercase tracking-widest text-slate-400">Waiting for dispatcher to assign work</div>
          </div>
        )}

        {active.length > 0 && (
          <div className="font-mono text-sm uppercase tracking-[0.3em] text-slate-400 pt-1">Your route · {active.length} stops</div>
        )}

        <AnimatePresence initial={false}>
          {active.map((job, i) => {
            const isNext = i === 0;
            return (
              <motion.div
                key={job.id}
                id={`job-${job.id}`}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, delay: Math.min(i * 0.04, 0.2) }}
                className={
                  isNext
                    ? "relative border-l-[6px] border-[#F59E0B] bg-[#172554] rounded-r-xl rounded-l-sm shadow-[0_8px_30px_rgba(245,158,11,0.18)] ring-1 ring-amber-500/20"
                    : "relative border border-slate-700/60 bg-[#1E293B]/80 rounded-xl"
                }
                data-testid={`job-card-${job.delivery_number}`}
              >
                {isNext && (
                  <div className="absolute -top-3 left-4 bg-[#F59E0B] text-black font-mono text-[11px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded" data-testid={`next-stop-flag-${job.delivery_number}`}>
                    Next Stop
                  </div>
                )}

                <div className={`${isNext ? "p-5 sm:p-6 pt-6" : "p-4"} flex items-start justify-between gap-3 border-b border-slate-700/50`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`shrink-0 flex items-center justify-center font-display font-black ${isNext ? "w-14 h-14 text-4xl bg-[#F59E0B] text-black" : "w-11 h-11 text-2xl bg-slate-700 text-slate-100"} rounded-lg`} data-testid={`seq-${job.delivery_number}`}>{i + 1}</div>
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-amber-300 font-bold tracking-widest">{job.delivery_number}</div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <PriorityTag priority={job.service_priority} />
                        <StatusBadge status={job.status} />
                        {job.special_instructions && (
                          <span className="font-mono text-xs uppercase tracking-widest px-2.5 py-1 border border-amber-500 text-amber-300 bg-amber-500/10 rounded flex items-center gap-1" data-testid={`special-flag-${job.delivery_number}`}>
                            <AlertTriangle className="w-3.5 h-3.5" /> Special
                          </span>
                        )}
                        {job.status === "in_transit" && isReattempt(job) && (
                          <span className="font-mono text-xs uppercase tracking-widest px-2.5 py-1 border border-amber-500 text-amber-300 bg-amber-500/10 rounded flex items-center gap-1" data-testid={`reattempt-flag-${job.delivery_number}`}>
                            <RotateCcw className="w-3.5 h-3.5" /> 2nd Attempt
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button disabled={i === 0} onClick={() => reorder(i, -1)} className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0F172A] text-slate-400 hover:text-white active:bg-slate-700 disabled:opacity-30 transition-colors touch-manipulation" data-testid={`move-up-${job.delivery_number}`}><ArrowUp className="w-5 h-5" /></button>
                    <button disabled={i === active.length - 1} onClick={() => reorder(i, 1)} className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0F172A] text-slate-400 hover:text-white active:bg-slate-700 disabled:opacity-30 transition-colors touch-manipulation" data-testid={`move-down-${job.delivery_number}`}><ArrowDown className="w-5 h-5" /></button>
                  </div>
                </div>

                <div role="button" tabIndex={0} onClick={() => setDetailsJob(job)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailsJob(job); } }} className={`w-full text-left ${isNext ? "p-5 sm:p-6" : "p-4"} space-y-4 border-b border-slate-700/50 hover:bg-white/5 transition touch-manipulation cursor-pointer`} data-testid={`details-btn-${job.delivery_number}`}>
                  <AddressBlock job={job} big={isNext} />
                  <div className="font-mono text-sm text-slate-400 border-t border-dashed border-slate-700 pt-3">
                    {job.package_description || "Package"} · {job.weight_kg}kg · {job.pieces}pc
                  </div>
                  <div className="font-mono text-xs uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
                    <Info className="w-4 h-4" /> Tap for package info, instructions &amp; documents
                  </div>
                </div>

                <div className={`${isNext ? "p-5 sm:p-6" : "p-4"}`}>
                  <Actions job={job} />
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {delivered.length > 0 && (
          <div className="pt-6 mt-2 border-t border-slate-800">
            <div className="font-mono text-sm uppercase tracking-[0.3em] text-slate-400 mb-3">Completed</div>
            {delivered.slice(0, 8).map((j) => (
              <div key={j.id} className="border border-slate-800 bg-[#141c2e] rounded-xl p-4 mb-3 flex items-center justify-between gap-3" data-testid={`done-row-${j.delivery_number}`}>
                <div className="min-w-0">
                  <div className="font-mono text-xs text-emerald-400 tracking-widest">{j.delivery_number}</div>
                  <div className="text-lg font-bold text-slate-200 leading-tight truncate">{j.dropoff.address}</div>
                  {j.recipient_name && <div className="font-mono text-xs text-slate-400 flex items-center gap-1 mt-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> {j.recipient_name}</div>}
                </div>
                <button onClick={() => revisit(j)} className="min-h-[48px] px-4 rounded-lg border-2 border-slate-600 bg-slate-800/60 text-slate-200 hover:bg-slate-700 font-mono uppercase text-xs flex items-center gap-1.5 shrink-0 active:scale-95 transition-transform touch-manipulation" data-testid={`revisit-btn-${j.delivery_number}`}>
                  <RotateCcw className="w-4 h-4" />Revisit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job details */}
      <Dialog open={!!detailsJob} onOpenChange={(o) => !o && setDetailsJob(null)}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto bg-[#1E293B] border-2 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tighter text-2xl text-white">Job Details</DialogTitle>
            <DialogDescription className="sr-only">Package information, instructions and attached documents</DialogDescription>
          </DialogHeader>
          {detailsJob && (
            <div className="space-y-6 pt-1">
              <div className="space-y-1.5">
                <div className="uppercase tracking-widest text-slate-400 text-xs font-mono">Package</div>
                <div className="text-lg font-semibold text-white">{detailsJob.package_description || "Package"} · {detailsJob.weight_kg}kg · {detailsJob.pieces}pc</div>
                {detailsJob.po_number && <div className="text-base text-slate-300 font-mono">PO: {detailsJob.po_number}</div>}
              </div>

              <div className="border-t border-dashed border-slate-700 pt-5 space-y-5">
                <div className="font-mono text-sm uppercase tracking-widest text-amber-300">Instructions</div>
                {(() => {
                  const p = detailsJob.pickup.instructions?.trim();
                  const d = detailsJob.dropoff.instructions?.trim();
                  const s = detailsJob.special_instructions?.trim();
                  if (!p && !d && !s) {
                    return <div className="text-lg text-slate-400" data-testid="detail-no-instructions">No specific instructions</div>;
                  }
                  return (
                    <>
                      {p && (
                        <div className="space-y-1.5">
                          <div className="font-mono text-xs uppercase tracking-widest text-slate-400">Pickup Instructions</div>
                          <div className="text-lg leading-snug text-white" data-testid="detail-pickup-instructions">{p}</div>
                        </div>
                      )}
                      {d && (
                        <div className="space-y-1.5">
                          <div className="font-mono text-xs uppercase tracking-widest text-slate-400">Delivery Instructions</div>
                          <div className="text-lg leading-snug text-white" data-testid="detail-dropoff-instructions">{d}</div>
                        </div>
                      )}
                      {s && (
                        <div className="space-y-1.5">
                          <div className="font-mono text-xs uppercase tracking-widest text-slate-400">Special Instructions</div>
                          <div className="text-lg leading-snug text-amber-300 font-semibold" data-testid="detail-special-instructions">⚠ {s}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="border-t border-dashed border-slate-700 pt-5">
                <div className="font-mono text-sm uppercase tracking-widest text-amber-300 mb-3">Attached Documents</div>
                {detailsJob.documents && detailsJob.documents.length > 0 ? (
                  <div className="space-y-2">
                    {detailsJob.documents.map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-slate-700 rounded-lg p-3 hover:border-amber-500 text-base text-white min-h-[56px]" data-testid={`doc-${i}`}>
                        <FileText className="w-5 h-5 text-amber-300 shrink-0" /> {d.name || `Document ${i + 1}`}
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-base text-slate-500">No documents attached</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* POD step 1: signature + recipient name (mandatory) */}
      <Dialog open={podStep === 1} onOpenChange={(o) => !o && setPodStep(0)}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto bg-[#1E293B] border-2 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tighter text-2xl text-white">Proof of Delivery</DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-widest text-slate-400">Recipient name and signature are required</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest mb-2 text-slate-300">Recipient Name <span className="text-red-400">*</span></div>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Who received the package?" className="h-14 text-lg bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#E56A00]" data-testid="recipient-name-input" />
            </div>
            <div>
              <div className="font-mono text-xs uppercase tracking-widest mb-2 text-slate-300">Signature <span className="text-red-400">*</span></div>
              <SignaturePad onChange={setSignature} />
            </div>
            <button onClick={continueToPhoto} className={primaryBtn} data-testid="pod-continue-btn">
              Continue <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* POD step 2: photo (optional) */}
      <Dialog open={podStep === 2} onOpenChange={(o) => !o && setPodStep(0)}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto bg-[#1E293B] border-2 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tighter text-2xl text-white">Add a Photo?</DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-widest text-slate-400">Optional proof photo of the delivered package</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="block border-2 border-dashed border-slate-700 rounded-xl p-6 text-center cursor-pointer hover:border-[#E56A00] transition bg-[#0F172A]">
              <Camera className="w-9 h-9 mx-auto mb-2 text-slate-400" strokeWidth={1.5} />
              <div className="font-mono text-xs uppercase tracking-widest text-slate-400">{photo ? "Photo captured — tap to retake" : "Tap to capture"}</div>
              <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="photo-input" />
            </label>
            {photo && <img src={photo} alt="proof" className="w-full max-h-40 object-cover rounded-lg border border-slate-700" />}
            <div>
              <div className="font-mono text-xs uppercase tracking-widest mb-2 text-slate-300">Notes (optional)</div>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Left at front door" className="text-base bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#E56A00]" data-testid="proof-notes-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={submitProof} disabled={busy} className={`${secondaryBtn} disabled:opacity-50`} data-testid="pod-skip-btn">Skip &amp; Confirm</button>
              <button onClick={submitProof} disabled={busy || !photo} className={`${primaryBtn} disabled:opacity-50 disabled:active:scale-100`} data-testid="confirm-delivery-btn">
                Confirm with Photo
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
